import { ConfirmDeletionDialog } from "@/components/ConfirmDeletionDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Integration } from "@/constants/integrations";
import { getProviderAuthUrl, getToken } from "@/lib/app/tokens";
import { deleteToken } from "@/lib/supabase/tokens";
import { NestedToken, SignedInCompany } from "@revelate/types";
import { XIcon, LinkIcon } from "lucide-react";
import { KeyedMutator } from "swr";
import { Link } from "wouter";
// import QRCode from "react-qr-code";
// import { LiveSyncStatus } from "./live-sync-status";
import { LastDealSyncStatus } from "./last-deal-sync-status";

interface IntegrationCardProps {
  integration: Integration;
  tokens: NestedToken[];
  company: SignedInCompany;
  mutate: KeyedMutator<NestedToken[]>;
}

export function IntegrationCard({
  integration,
  tokens,
  company,
  mutate,
}: IntegrationCardProps) {
  const {
    name,
    description,
    descriptionConnected,
    icon,
    id,
    available,
    // updateSupported,
    // liveSync,
  } = integration || {};

  const token = getToken(tokens, id);
  const tokenId = token?.id.toString() || "";

  return (
    <Card className="h-[400px] flex flex-col">
      <CardHeader>
        <div className="flex flex-row justify-between h-20">
          {/* Logo */}
          <div className="h-20 w-40 p-2 flex justify-center items-center border border-gray-200 rounded-xl">
            <img src={icon} alt={name} className="w-36 max-h-16" />
          </div>
          {/* Reconnection QR code */}
          {/* available && !token && (
            <div className="align-left pb-5">
              <QRCode
                opacity={1}
                className="w-auto h-20"
                value={getProviderAuthUrl(company.id, id)}
              />
            </div>
          ) */}
        </div>
      </CardHeader>

      <CardContent className="flex-1">
        {/* Name and connection status */}
        <CardTitle className="text-xl">
          <div className="flex flex-row items-center mb-1">
            <h2 className="mr-2 align-middle">
              {name}
            </h2>
            {available && (
              <Badge
                variant={
                  token
                    ? token.is_expired
                      ? "unapproved"
                      : "paid"
                    : "secondary"
                }
              >
                {token
                  ? token.is_expired
                    ? "Connection expired"
                    : "Connected"
                  : "Not connected"}
              </Badge>
            )}
          </div>
        </CardTitle>
        <CardDescription>
          {token && !token.is_expired ? descriptionConnected : description}
        </CardDescription>

        {/* token && !token.is_expired && (
          <UpdateCheckbox
            integration={integration}
            mutate={mutate}
            token={token}
          />
        )*/}

        {/* liveSync.available && available && token && (
          <LiveSyncStatus providerId={id} syncMethod={liveSync.method} />
        ) */}

        {/* FIXME: Hitting RLS */}
        {token && <LastDealSyncStatus providerId={id} />}

      </CardContent>

      <CardFooter className="grid gap-4">
        {!available ? (
          <Button disabled className="w-full" variant="outline">
            Unavailable
          </Button>
        ) : token && !token.is_expired ? (
          <ConfirmDeletionDialog
            key={`delete-${tokenId}`}
            title={`Are you sure you want to disconnect ${name}?`}
            description="You will have to reconnect the provider to sync data again."
            deleteId={tokenId}
            deleteOperation={async (tokenId) => {
              await deleteToken(Number(tokenId));
              const updatedTokens = tokens.filter(
                (token) => token.id !== Number(tokenId)
              );
              mutate(updatedTokens);
            }}
          >
            <Button variant="outline">
              <XIcon className="mr-2 h-4 w-4" />
              Disconnect
            </Button>
          </ConfirmDeletionDialog>
        ) : (
          <Link href={getProviderAuthUrl(company.id, id)} target="_blank">
            <Button
              onClick={() =>
                window.open(getProviderAuthUrl(company.id, id), "_blank")
              }
              className="w-full"
              variant={token?.is_expired ? "secondary" : "default"}
            >
              {token?.is_expired ? "Reconnect" : "Connect"}
            </Button>
          </Link>
        )}
      </CardFooter>
    </Card>
  );
}
